import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const isTouch = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window);

/**
 * Premium engineered cursor — a precision dot + an inertia-lagged
 * crosshair ring with CAD tick marks. Reactive variants:
 *   default   – tiny dot + 26px ring + 4 tick marks
 *   hover     – ring expands to 56px with subtle orange glow
 *   precision – dot shrinks for fine-line over headings / large copy
 *   reflect   – ring scales further + a soft light-reflect halo for visuals
 */
export const Cursor = () => {
  const [enabled, setEnabled] = useState(false);
  const [variant, setVariant] = useState("default");
  const [hidden, setHidden] = useState(false);
  const labelRef = useRef("");
  const [, force] = useState(0);

  // Raw, un-spring positions for the dot — pixel-exact.
  const x = useMotionValue(-200);
  const y = useMotionValue(-200);

  // Lagged positions for the ring — inertia spring.
  const ringX = useSpring(x, { stiffness: 320, damping: 28, mass: 0.6 });
  const ringY = useSpring(y, { stiffness: 320, damping: 28, mass: 0.6 });

  // Trail — even more lag, smaller scale.
  const trailX = useSpring(x, { stiffness: 140, damping: 22, mass: 0.7 });
  const trailY = useSpring(y, { stiffness: 140, damping: 22, mass: 0.7 });

  // Variant dimensions
  const ringSize =
    variant === "hover" ? 56 : variant === "reflect" ? 72 : variant === "precision" ? 18 : 26;
  const dotSize = variant === "precision" ? 2 : variant === "hover" ? 3 : 4;

  // --- Spark trail emitter ------------------------------------------------
  const sparksRef = useRef(null);
  const lastPos = useRef({ x: -200, y: -200, t: 0 });

  const emitSpark = (cx, cy, vx, vy) => {
    const c = sparksRef.current;
    if (!c) return;
    // Sparks fly OPPOSITE to cursor direction (trailing behind it).
    const angle = Math.atan2(vy, vx) + Math.PI + (Math.random() - 0.5) * 0.55;
    const dist = 26 + Math.random() * 42;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const dur = 0.42 + Math.random() * 0.28;
    const s = document.createElement("span");
    s.className = "cursor-spark";
    s.style.left = `${cx}px`;
    s.style.top = `${cy}px`;
    s.style.setProperty("--vx", `${dx.toFixed(1)}px`);
    s.style.setProperty("--vy", `${dy.toFixed(1)}px`);
    s.style.animationDuration = `${dur.toFixed(2)}s`;
    c.appendChild(s);
    s.addEventListener("animationend", () => s.remove(), { once: true });
  };

  useEffect(() => {
    if (isTouch()) return;
    setEnabled(true);

    const root = document.documentElement;
    root.classList.add("cursor-none");

    const onMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);

      // Velocity-driven spark emission.
      const now = performance.now();
      const dt = now - lastPos.current.t;
      if (dt >= 9) {
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        const speed = Math.hypot(dx, dy) / Math.max(1, dt); // px / ms
        if (speed > 0.18 && lastPos.current.t > 0) {
          // Density: ~1 spark for normal motion, 2 for fast bursts. Keep premium.
          const n = speed > 1.4 ? 2 : 1;
          for (let i = 0; i < n; i++) {
            emitSpark(e.clientX, e.clientY, dx / dt, dy / dt);
          }
        }
        lastPos.current = { x: e.clientX, y: e.clientY, t: now };
      }
    };

    const onOver = (e) => {
      const t = e.target;
      // Reflect on imagery / explicit data-cursor="reflect"
      if (
        t.closest('[data-cursor="reflect"]') ||
        t.closest("img") ||
        t.closest("video")
      ) {
        labelRef.current = t.closest("[data-cursor-label]")?.dataset?.cursorLabel || "";
        setVariant("reflect");
        force((n) => n + 1);
        return;
      }
      // Buttons / links / form fields → hover
      if (
        t.closest(
          'a, button, [role="button"], input, textarea, select, [data-cursor="hover"]'
        )
      ) {
        labelRef.current = t.closest("[data-cursor-label]")?.dataset?.cursorLabel || "";
        setVariant("hover");
        force((n) => n + 1);
        return;
      }
      // Large headings → precision
      if (t.closest('h1, h2, [data-cursor="precision"]')) {
        labelRef.current = "";
        setVariant("precision");
        force((n) => n + 1);
        return;
      }
      labelRef.current = "";
      setVariant("default");
      force((n) => n + 1);
    };

    const onLeave = () => setHidden(true);
    const onEnter = () => setHidden(false);
    const onDown = () => setVariant((v) => v + ":down");
    const onUp = () =>
      setVariant((v) => (v.endsWith(":down") ? v.replace(":down", "") : v));

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseover", onOver);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);

    return () => {
      root.classList.remove("cursor-none");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
    };
  }, [x, y]);

  // Reactive halo opacity for "reflect" — subtle light pool
  const reflectOpacity = useTransform(ringX, () => (variant === "reflect" ? 1 : 0));

  if (!enabled) return null;

  const baseVariant = variant.replace(":down", "");
  const isDown = variant.endsWith(":down");

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[9999]"
      style={{ opacity: hidden ? 0 : 1, transition: "opacity 0.2s linear" }}
    >
      {/* Spark trail container — particles are appended directly to the DOM
          for performance (no React re-render per particle). */}
      <div
        ref={sparksRef}
        aria-hidden
        className="absolute inset-0 pointer-events-none"
      />
      {/* Reflect halo — pool of warm light for imagery */}
      <motion.div
        style={{
          x: trailX,
          y: trailY,
          translateX: "-50%",
          translateY: "-50%",
          opacity: reflectOpacity,
        }}
        className="absolute top-0 left-0 w-60 h-60 rounded-full"
      >
        <div
          className="w-full h-full rounded-full"
          style={{
            background:
              "radial-gradient(circle at center, rgba(255,140,60,0.18), rgba(255,87,34,0.08) 40%, transparent 70%)",
            filter: "blur(8px)",
          }}
        />
      </motion.div>

      {/* Outer ring with CAD tick marks */}
      <motion.div
        style={{
          x: ringX,
          y: ringY,
          translateX: "-50%",
          translateY: "-50%",
          width: ringSize,
          height: ringSize,
        }}
        animate={{ scale: isDown ? 0.85 : 1 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
        className="absolute top-0 left-0"
      >
        <div
          className="relative w-full h-full rounded-full transition-[box-shadow,border-color,background] duration-300"
          style={{
            border: "1px solid rgba(255,255,255,0.55)",
            background:
              baseVariant === "hover" || baseVariant === "reflect"
                ? "rgba(255,87,34,0.06)"
                : "transparent",
            boxShadow:
              baseVariant === "hover"
                ? "0 0 22px rgba(255,87,34,0.45), inset 0 0 0 1px rgba(255,87,34,0.45)"
                : baseVariant === "reflect"
                  ? "0 0 18px rgba(255,140,60,0.35)"
                  : "none",
          }}
        >
          {/* CAD tick marks at 12 / 3 / 6 / 9 — only in default + reflect */}
          {(baseVariant === "default" || baseVariant === "reflect") && (
            <>
              <span className="absolute left-1/2 -top-1.5 w-px h-1.5 -translate-x-1/2 bg-white/70" />
              <span className="absolute left-1/2 -bottom-1.5 w-px h-1.5 -translate-x-1/2 bg-white/70" />
              <span className="absolute top-1/2 -left-1.5 h-px w-1.5 -translate-y-1/2 bg-white/70" />
              <span className="absolute top-1/2 -right-1.5 h-px w-1.5 -translate-y-1/2 bg-white/70" />
            </>
          )}

          {/* Optional contextual label that rides next to the ring */}
          {labelRef.current && (
            <span
              className="absolute top-full left-1/2 mt-3 -translate-x-1/2 whitespace-nowrap text-[9px] tracking-[0.3em] uppercase font-mono text-white/80"
              style={{
                color:
                  baseVariant === "hover" ? "#FF8A3C" : "rgba(255,255,255,0.8)",
              }}
            >
              {labelRef.current}
            </span>
          )}
        </div>
      </motion.div>

      {/* Inner precision dot */}
      <motion.div
        style={{
          x,
          y,
          translateX: "-50%",
          translateY: "-50%",
          width: dotSize,
          height: dotSize,
        }}
        className="absolute top-0 left-0 rounded-full bg-white"
      />
    </div>
  );
};
